import React, { useState, useEffect } from 'react';
import { ToggleSwitch, Modal, TextInput } from 'flowbite-react';
import { Button } from '../../common';
import BinIcon from '../../../assets/icons/DeleteIcon';
import PlusIcon from '../../../assets/icons/PlusIcon';
import LeftArrowIcon from '../../../assets/icons/LeftArrowIcon';

interface WeeklyReportSettingsProps {
  settings: {
    weeklyReport: {
      email: string[];
    };
  };
  userEmail: string;
  onSettingsChange: (newSettings: any) => Promise<void>;
  showBanner: (message: string) => void;
}

export const WeeklyReportSettings: React.FC<WeeklyReportSettingsProps> = ({ 
  settings, 
  userEmail,
  onSettingsChange,
  showBanner
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emails, setEmails] = useState(settings.weeklyReport.email?.length > 0 ? settings.weeklyReport.email : ['']);
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [hasEmptyEmailError, setHasEmptyEmailError] = useState(false);

  useEffect(() => {
    const isValid = emails.every(email => {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email.trim() === '' || re.test(email.trim());
    });
    setHasValidationError(!isValid);

    const hasAtLeastOneEmail = emails.some(email => email.trim() !== '');
    setHasEmptyEmailError(!hasAtLeastOneEmail);
  }, [emails]);

  const handleToggle = async (value: boolean) => {
    if (value) {
      if (settings.weeklyReport?.email?.length === 0) {
        setEmails(['']); 
      }
      setShowEmailModal(true);
    } else {
      const updatedSettings = {
        ...settings,
        weeklyReport: {
          email: []
        }
      };
      await onSettingsChange(updatedSettings);
      showBanner('Weekly reports disabled successfully');
    }
  };

  const handleAddEmail = () => {
    if (emails.length === 1 && emails[0].trim() === '') {
      setHasEmptyEmailError(true);
      return;
    }

    if (newEmail && !emails.includes(newEmail)) {
      setEmails([...emails, newEmail]);
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    if (emails.length === 1) {
      setHasEmptyEmailError(true);
      return;
    }
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleSaveEmails = async () => {
    const validEmails = emails.filter(email => email.trim() !== '');
    
    if (validEmails.length === 0) {
      setHasEmptyEmailError(true);
      return;
    }

    setIsLoading(true);
    try {
      const updatedSettings = {
        ...settings,
        weeklyReport: {
          email: validEmails
        }
      };
      await onSettingsChange(updatedSettings);
      setShowEmailModal(false);
      showBanner('Weekly report settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      showBanner('Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const isSaveDisabled = hasValidationError || hasEmptyEmailError;

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-4'>
        <h1 className='text-xl font-semibold'>Send Weekly Business Health Report</h1>
        <ToggleSwitch 
          checked={settings.weeklyReport?.email?.length > 0} 
          onChange={handleToggle} 
          color="blue"
        />
      </div>
      <p className=''>Your weekly business summary will be sent automatically. Share it with your partners to keep them informed.</p>

      {settings.weeklyReport?.email?.length > 0 && (
        <div className="mt-4 p-4 rounded-lg">
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-500 mb-4">Send via mail to:</p>
            <div className='flex gap-2 w-full'>
              <LeftArrowIcon />
              <div className='bg-gray-50 p-4 flex gap-2 rounded-md w-full'>
                <div className='flex flex-col w-full'>
                  {settings.weeklyReport.email.map((email) => (
                    <div key={email} className="flex flex-col">
                      <label htmlFor="" className='text-sm mt-[10px]'>Email Address</label>
                      <p className="text-sm bg-white w-full p-3 rounded-md">{email}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p 
              onClick={() => {
                setEmails(settings.weeklyReport.email);
                setShowEmailModal(true);
              }}
              className='flex justify-end text-[#044E97] font-semibold cursor-pointer'
            >
              Edit
            </p>
          </div>
        </div>
      )}

      <Modal show={showEmailModal} onClose={() => setShowEmailModal(false)}>
        <Modal.Header>Send Weekly Business Health Report</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
              Your weekly business summary will be sent automatically. Share it with your partners to keep them informed.
            </p>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Who do you want to share it with</p>
              <div className='bg-gray-100 p-4 rounded-md border'>
                {emails.map((email, index) => (
                  <div key={index} className="mb-2">
                    <div className="flex gap-2 items-center">
                      <div className="flex w-full items-center"> 
                        <TextInput
                          type="email"
                          value={email}
                          placeholder={index === 0 ? "Enter your email" : ""}
                          onChange={(e) => {
                            const updatedEmails = [...emails];
                            updatedEmails[index] = e.target.value;
                            setEmails(updatedEmails);
                          }}
                          className="w-[450px]"
                          color={email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'failure' : undefined}
                          helperText={email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? (
                            <span className="text-red-500">Please enter a valid email</span>
                          ) : null}
                        />
                      </div>
                      <div className='w-full'>
                        {index > 0 && (
                          <p
                            onClick={() => handleRemoveEmail(email)}
                            className='cursor-pointer'
                          >
                            <BinIcon />
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="mt-4">
                  <p
                    onClick={() => {
                      if (emails.length === 1 && emails[0].trim() === '') {
                        setHasEmptyEmailError(true);
                        return;
                      }
                      setEmails([...emails, '']);
                    }}
                    className="w-full text-[#044E97] flex gap-2 items-center font-semibold cursor-pointer"
                  >
                    <PlusIcon/>
                    <span>Add New Email</span> 
                  </p>
                </div>
              </div>
              {hasValidationError && (
                <p className="mt-2 text-sm text-red-500">
                  Please enter valid email addresses
                </p>
              )}
              {hasEmptyEmailError && (
                <p className="mt-2 text-sm text-red-500">
                  Please enter at least one email address before adding more
                </p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='flex justify-end gap-4'>
          <Button 
            size='small' 
            variant='white' 
            outline={true} 
            onClick={() => setShowEmailModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            size='small' 
            variant='secondary' 
            onClick={handleSaveEmails}
            disabled={isSaveDisabled || isLoading}
            isLoading={isLoading}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};